import { generateGuid } from 'global/utils';
import update from 'immutability-helper';
import uniq from 'lodash/uniq';
import { SECTION_ACCESS_TYPES } from '../utils/consts';
import {
    ACTIVE_MEMBER_TYPE_CHANGED,
    ADD_FUTURE_CHANGED,
    ADD_MESSAGE_TO_GROUP,
    APP_SELECTION_CHANGE,
    APP_SITE_SELECTION_CHANGE,
    CLEAN_MESSAGES,
    CLOSE_AGENCY_DIALOG,
    CLOSE_CONFIGURATION_REQUIRED_DIALOG,
    CLOSE_SHELF,
    CREATE_NEW_MEMBER,
    DATA_SOURCE_SELECTION_CHANGE,
    DATA_SOURCE_SELECTION_CHANGE_ALL,
    DELETE_USER_DISABLED,
    DELETE_USER_INVITE_REVOKED,
    DIMENSION_FILTER_SELECTION_CHANGE,
    HIDE_PAGE,
    MANAGE_PERMISSIONS,
    MANAGE_PERMISSIONS_FINISHED,
    METRIC_SELECTION_CHANGE,
    MODIFY_INVITATION_STATE,
    ORG_USERS_LOADED,
    SAVE_MEMBER_ERROR,
    SAVE_MEMBER_SUCCESS,
    SCREENS_SELECTION_TOGGLE,
    SELECTED_TAB_INDEX_CHANGED,
    STEP_VALIDATION_STATUS_CHANGED,
    TEAMMANAGEMENT_PAGE_LOADED,
    UPDATE_AGENCY_SELECTION_FORM,
    UPDATE_APPS_LIST,
    UPDATE_DATA_SOURCE_DATA,
    UPDATE_FEATURES_DICT,
    UPDATE_PARTNERS_DATA,
    UPDATE_ROLE_SELECTION_FORM,
    UPDATE_SECTION_PERMISSIONS,
    UPDATE_SECTIONS_DICT,
    UPDATE_USER_REGISTRATION_FORM,
    WIZARD_ACTION_TYPE_CHANGED,
} from './actions';
import { SECTIONS_PAGES_CONFIG } from './sections';
import { DEFAULT_AGENCY_SCREEN_SELECTION, MEMBER_TYPES, ROLE_AGENCY, ROLE_STANDARD, WIZARD_MODES } from './utils';

update.extend('$auto', (value, object) => {
    return object ? update(object, value) : update({}, value);
});
update.extend('$autoArray', (value, object) => {
    return object ? update(object, value) : update([], value);
});

export const initialState = {
    pageLoaded: false,
    orgUsers: [],
    messages: {
        pending: [],
        active: [],
    },
    uans: {},
    partners: [],
    appsList: [],
    screensDict: {},
    messagesDefs: {},
    shelf: {
        open: false,
    },
    page: {
        show: false,
        loading: false,
        selectedTabIndex: 0,
        stepValidationStatus: {
            isValid: false,
            title: '',
        },
    },
    agencyDialog: {
        open: false,
    },
    editMemberId: null,
    userRegistrationForm: {
        firstName: {
            value: '',
        },
        lastName: {
            value: '',
        },
        email: {
            value: '',
        },
        description: {
            value: '',
        },
    },
    agencySelectionForm: {
        id: '',
        name: '',
    },
    dimensionFilters: {},
    selectedMetrics: {
        values: [],
        addFuture: false,
    },
    roleSelectionForm: {
        role: ROLE_STANDARD,
    },
    screenSelectionForm: {
        selectedScreens: [],
        addFuture: false,
    },
    wizard: {
        mode: '',
        currentActionType: 'load',
    },
    saveMemberError: '',
    activeMemberType: MEMBER_TYPES.USER,
    sectionPermissions: [],
    configurationRequiredDialog: {
        open: false,
        pages: [],
    },
};

const selectionToggleFunc = (action, idsKey = 'appIDs', items) => {
    let ret = items;
    if (action.value === false) {
        ret = [...items].filter(id => !action[idsKey].includes(id));
    }
    if (action.value === true) {
        ret = [...items, ...action[idsKey]];
    }
    return uniq(ret);
};

const teamManagement = (state = initialState, action) => {
    switch (action.type) {
        case TEAMMANAGEMENT_PAGE_LOADED: {
            return update(state, {
                pageLoaded: {
                    $set: true,
                },
            });
        }
        case ORG_USERS_LOADED: {
            return update(state, {
                orgUsers: {
                    $set: action.orgUsers,
                },
            });
        }
        case UPDATE_FEATURES_DICT: {
            return update(state, {
                screensDict: {
                    $set: action.featuresDict,
                },
            });
        }
        case UPDATE_SECTIONS_DICT: {
            return update(state, {
                sectionsDict: {
                    $set: action.sectionsDict,
                },
            });
        }
        case DELETE_USER_INVITE_REVOKED: {
            const { user } = action;
            const userIndex = state.orgUsers.findIndex(ou => ou.invite_id === user.invite_id);
            return update(state, {
                orgUsers: {
                    $splice: [[userIndex, 1]],
                },
            });
        }
        case DELETE_USER_DISABLED: {
            const { user } = action;
            const userIndex = state.orgUsers.findIndex(ou => ou.user_id === user.user_id);
            return update(state, {
                orgUsers: {
                    $splice: [[userIndex, 1, { ...state.orgUsers[userIndex], status: 'Deactivated' }]],
                },
            });
        }
        case CLEAN_MESSAGES: {
            return update(state, {
                messages: Object.keys(state.messages).reduce((total, groupName) => {
                    total[groupName] = { $set: [] };
                    return total;
                }, {}),
            });
        }
        case ADD_MESSAGE_TO_GROUP: {
            const { group, message, messageType } = action;
            const guid = generateGuid();
            // Changing behaviour per product request. removing other notifications when a new one introduced
            const otherGroups = Object.keys(state.messages)
                .filter(m => m !== group)
                .reduce((total, groupName) => {
                    total[groupName] = {
                        $set: [],
                    };
                    return total;
                }, {});

            return update(state, {
                messages: {
                    [group]: {
                        $set: [guid],
                    },
                    ...otherGroups,
                },
                messagesDefs: {
                    [guid]: {
                        $set: {
                            message,
                            type: messageType,
                        },
                    },
                },
            });
        }
        case CREATE_NEW_MEMBER: {
            return update(state, {
                shelf: {
                    open: { $set: state.activeMemberType === MEMBER_TYPES.USER },
                },
                agencyDialog: {
                    open: { $set: state.activeMemberType === MEMBER_TYPES.AGENCY },
                },
                userRegistrationForm: {
                    $set: initialState.userRegistrationForm,
                },
                agencySelectionForm: {
                    $set: initialState.agencySelectionForm,
                },
                roleSelectionForm: {
                    role: {
                        $set: state.activeMemberType === MEMBER_TYPES.AGENCY ? ROLE_AGENCY : ROLE_STANDARD,
                    },
                },
                dimensionFilters: {
                    $set: {},
                },
                selectedMetrics: {
                    values: {
                        $set: [],
                    },
                    addFuture: {
                        $set: false,
                    },
                },
                screenSelectionForm: {
                    selectedScreens: {
                        $set: state.activeMemberType === MEMBER_TYPES.AGENCY ? DEFAULT_AGENCY_SCREEN_SELECTION : [],
                    },
                    addFuture: {
                        $set: false,
                    },
                },
                wizard: {
                    mode: {
                        $set: WIZARD_MODES.create,
                    },
                },
                sectionPermissions: {
                    $set: [],
                },
                configurationRequiredDialog: {
                    open: { $set: false },
                    pages: { $set: [] },
                },
            });
        }
        case CLOSE_SHELF: {
            return update(state, {
                shelf: {
                    open: { $set: false },
                },
                editMemberId: {
                    $set: null,
                },
                wizard: {
                    mode: {
                        $set: '',
                    },
                    currentActionType: {
                        $set: 'load',
                    },
                },
                inviteError: {
                    $set: '',
                },
            });
        }
        case HIDE_PAGE: {
            return update(state, {
                page: {
                    show: { $set: false },
                    loading: { $set: false },
                    selectedTabIndex: { $set: 0 },
                    stepValidationStatus: {
                        $set: { isValid: false, title: '' },
                    },
                },
                editMemberId: {
                    $set: null,
                },
                inviteError: {
                    $set: '',
                },
            });
        }
        case SELECTED_TAB_INDEX_CHANGED: {
            return update(state, {
                page: {
                    selectedTabIndex: { $set: action.selectedTabIndex },
                },
            });
        }
        case STEP_VALIDATION_STATUS_CHANGED: {
            return update(state, {
                page: {
                    stepValidationStatus: { $set: action.stepValidationStatus },
                },
            });
        }
        case CLOSE_AGENCY_DIALOG: {
            return update(state, {
                agencyDialog: {
                    open: { $set: false },
                },
            });
        }
        case MANAGE_PERMISSIONS: {
            const { user, partialState } = action;
            return update(state, {
                shelf: {
                    open: { $set: state.activeMemberType === MEMBER_TYPES.USER },
                },
                page: {
                    show: { $set: state.activeMemberType === MEMBER_TYPES.AGENCY },
                    selectedTabIndex: { $set: 0 },
                    stepValidationStatus: {
                        $set: { isValid: false, title: '' },
                    },
                },
                roleSelectionForm: {
                    $set: partialState.roleSelectionForm,
                },
                userRegistrationForm: {
                    $set: partialState.userRegistrationForm,
                },
                agencySelectionForm: {
                    $set: partialState.agencySelectionForm,
                },
                editMemberId: {
                    $set: partialState.editMemberId,
                },
                selectedMetrics: {
                    $set: partialState.selectedMetrics,
                },
                screenSelectionForm: {
                    $set: partialState.screenSelectionForm,
                },
                dimensionFilters: {
                    $set: partialState.dimensionFilters,
                },
                wizard: {
                    userData: { $set: user },
                    mode: {
                        $set: WIZARD_MODES.edit,
                    },
                },
                sectionPermissions: {
                    $set: partialState.sectionPermissions,
                },
                configurationRequiredDialog: {
                    open: { $set: false },
                    pages: { $set: [] },
                },
            });
        }
        case UPDATE_DATA_SOURCE_DATA: {
            return update(state, {
                uans: {
                    $set: action.data,
                },
            });
        }
        case UPDATE_PARTNERS_DATA: {
            return update(state, {
                partners: {
                    $set: action.partners,
                },
            });
        }
        case UPDATE_APPS_LIST: {
            return update(state, {
                appsList: {
                    $set: action.appsList,
                },
            });
        }
        case APP_SELECTION_CHANGE: {
            return update(state, {
                dimensionFilters: {
                    app: {
                        $auto: {
                            values: {
                                $autoArray: {
                                    $apply: selectionToggleFunc.bind(null, action, 'appIDs'),
                                },
                            },
                        },
                    },
                },
            });
        }
        case APP_SITE_SELECTION_CHANGE: {
            return update(state, {
                dimensionFilters: {
                    app_site_id: {
                        $auto: {
                            values: {
                                $autoArray: {
                                    $apply: selectionToggleFunc.bind(null, action, 'appSiteIDs'),
                                },
                            },
                        },
                    },
                },
            });
        }
        case SCREENS_SELECTION_TOGGLE: {
            return update(state, {
                screenSelectionForm: {
                    selectedScreens: {
                        $apply: selectionToggleFunc.bind(null, action, 'screenIds'),
                    },
                },
            });
        }
        case DATA_SOURCE_SELECTION_CHANGE: {
            return update(state, {
                dimensionFilters: {
                    base_source: {
                        $auto: {
                            values: {
                                $autoArray: {
                                    $apply: sources => {
                                        if (sources.includes(action.name) && action.value === false) {
                                            return [...sources].filter(id => id !== action.name);
                                        }
                                        if (!sources.includes(action.name) && action.value === true) {
                                            return [...sources, action.name];
                                        }
                                        return sources;
                                    },
                                },
                            },
                        },
                    },
                },
            });
        }
        case DATA_SOURCE_SELECTION_CHANGE_ALL: {
            return update(state, {
                dimensionFilters: {
                    base_source: {
                        $auto: {
                            values: {
                                $autoArray: {
                                    $apply: () => {
                                        if (action.value) {
                                            return action.allSources;
                                        } else {
                                            return [];
                                        }
                                    },
                                },
                            },
                        },
                    },
                },
            });
        }

        case DIMENSION_FILTER_SELECTION_CHANGE: {
            return update(state, {
                dimensionFilters: {
                    [action.name]: {
                        $auto: {
                            values: {
                                $set: action.values,
                            },
                        },
                    },
                },
            });
        }

        case METRIC_SELECTION_CHANGE: {
            return update(state, {
                selectedMetrics: {
                    values: {
                        $apply: metrics => {
                            if (action.value === false) {
                                return [...metrics].filter(id => !action.metricID.includes(id));
                            }
                            if (action.value === true) {
                                return [...new Set([...metrics, ...action.metricID])];
                            }
                            return metrics;
                        },
                    },
                },
            });
        }
        case UPDATE_USER_REGISTRATION_FORM: {
            return update(state, {
                userRegistrationForm: {
                    $set: action.fields,
                },
                saveMemberError: {
                    $set: '',
                },
            });
        }
        case UPDATE_AGENCY_SELECTION_FORM: {
            return update(state, {
                agencySelectionForm: {
                    $set: action.agency,
                },
                agencyDialog: {
                    open: { $set: false },
                },
                page: {
                    show: { $set: true },
                    selectedTabIndex: { $set: 0 },
                    stepValidationStatus: {
                        $set: { isValid: false, title: '' },
                    },
                },
            });
        }
        case UPDATE_ROLE_SELECTION_FORM: {
            return update(state, {
                roleSelectionForm: {
                    role: {
                        $set: action.role,
                    },
                },
            });
        }
        case SAVE_MEMBER_ERROR: {
            return update(state, {
                saveMemberError: {
                    $set: action.error,
                },
                page: {
                    loading: { $set: false },
                },
            });
        }
        case SAVE_MEMBER_SUCCESS: {
            return update(state, {
                shelf: {
                    open: { $set: false },
                },
                page: {
                    show: { $set: false },
                    loading: { $set: false },
                    selectedTabIndex: { $set: 0 },
                    stepValidationStatus: {
                        $set: { isValid: false, title: '' },
                    },
                },
                wizard: {
                    mode: {
                        $set: '',
                    },
                    currentActionType: {
                        $set: 'load',
                    },
                },
                editMemberId: {
                    $set: null,
                },
                configurationRequiredDialog: {
                    open: { $set: state.configurationRequiredDialog.pages.length > 0 },
                },
            });
        }
        case MODIFY_INVITATION_STATE: {
            const { user, userState } = action;
            const userIndex = state.orgUsers.findIndex(
                u => (user.user_id && u.user_id === user.user_id) || (user.invite_id && u.invite_id === user.invite_id)
            );
            const newUser = {
                ...user,
                ...userState,
            };
            return update(state, {
                orgUsers: {
                    $splice: [[userIndex, 1, newUser]],
                },
            });
        }
        case MANAGE_PERMISSIONS_FINISHED: {
            const viewAccessSectionNames = state.sectionPermissions
                .filter(sectionPermission => sectionPermission.access_type === SECTION_ACCESS_TYPES.VIEW)
                .map(sectionPermission => sectionPermission.name);

            const viewAccessPages = SECTIONS_PAGES_CONFIG.filter(({ cards }) =>
                viewAccessSectionNames.some(sectionName =>
                    cards.some(({ parts }) => parts.some(({ sectionNames }) => sectionNames.includes(sectionName)))
                )
            ).map(sectionPageConfig => sectionPageConfig.page);

            return update(state, {
                saveMemberError: {
                    $set: '',
                },
                page: {
                    loading: { $set: true },
                },
                configurationRequiredDialog: {
                    pages: {
                        $set: viewAccessPages,
                    },
                },
            });
        }
        case CLOSE_CONFIGURATION_REQUIRED_DIALOG: {
            return update(state, {
                configurationRequiredDialog: {
                    open: { $set: false },
                    pages: { $set: [] },
                },
            });
        }
        case WIZARD_ACTION_TYPE_CHANGED: {
            return update(state, {
                wizard: {
                    currentActionType: {
                        $set: action.actionType,
                    },
                },
            });
        }
        case ADD_FUTURE_CHANGED: {
            if (action.dimensionFilter) {
                return update(state, {
                    dimensionFilters: {
                        [action.form]: {
                            $auto: {
                                addFuture: {
                                    $set: action.value,
                                },
                            },
                        },
                    },
                });
            }

            return update(state, {
                [action.form]: {
                    addFuture: {
                        $set: action.value,
                    },
                },
            });
        }
        case ACTIVE_MEMBER_TYPE_CHANGED: {
            return update(state, {
                activeMemberType: {
                    $set: action.activeMemberType,
                },
            });
        }
        case UPDATE_SECTION_PERMISSIONS: {
            const newSectionPermissions = state.sectionPermissions.map(sectionPermission => ({
                ...sectionPermission,
                access_type: action.sectionNames.includes(sectionPermission.name)
                    ? action.accessType
                    : sectionPermission.access_type,
            }));

            for (const sectionName of action.sectionNames) {
                if (!newSectionPermissions.some(sectionPermission => sectionPermission.name === sectionName)) {
                    newSectionPermissions.push({
                        name: sectionName,
                        access_type: action.accessType,
                    });
                }
            }

            return update(state, {
                sectionPermissions: {
                    $set: newSectionPermissions,
                },
            });
        }
        default:
            return state;
    }
};

export default teamManagement;
